import { useEffect, type ReactNode } from "react"

import { usePageHeaderController } from "@/contexts/PageHeaderContext"

/**
 * Hook to set a custom page header in the SidebarNav.
 * The header is automatically cleared when the component unmounts.
 *
 * @param header - The header content to render
 * @param options.noPaddingBottom - Remove bottom padding so inline tabs sit on the border (default: false)
 */
export const useSetPageHeader = (
  header: ReactNode,
  options?: { noPaddingBottom?: boolean },
) => {
  const { setCustomPageHeader, setNoPaddingBottom } = usePageHeaderController()
  const noPaddingBottom = options?.noPaddingBottom ?? false

  useEffect(() => {
    setCustomPageHeader(header)
    setNoPaddingBottom(noPaddingBottom)

    return () => {
      setCustomPageHeader(null)
      setNoPaddingBottom(false)
    }
  }, [header, setCustomPageHeader, noPaddingBottom, setNoPaddingBottom])
}

/**
 * Hook to set the background of the SidebarNav page header (the top bar),
 * leaving the default breadcrumb header content in place. Useful when a page
 * uses a non-default page background and the top bar should match it rather
 * than show the app's default surface. Automatically reset on unmount.
 *
 * @param className - Tailwind background class to apply, or null for default
 */
export const useSetHeaderBackground = (className: string | null) => {
  const { setHeaderBackgroundClassName } = usePageHeaderController()

  useEffect(() => {
    setHeaderBackgroundClassName(className)

    return () => {
      setHeaderBackgroundClassName(null)
    }
  }, [className, setHeaderBackgroundClassName])
}
